import { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { AuthType } from 'modules/sendingPlatforms/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const ID = 'id';
const NAME = 'name';
const CUSTOM_MTA_CONFIG = 'customMTAConfig';
const API_ENDPOINT = 'apiEndpoint';
const AUTH_PARAMS = 'authParams';
const CLIENT_ID = 'clientId';
const CLIENT_SECRET = 'clientSecret';
const TOKEN_ENDPOINT = 'tokenEndpoint';
const AUTH_TYPE = 'authType';
const CUSTOM_HEADERS = 'customHeaders';

export const DESTINATION_FORM_FIELD_PATHS = {
  // Form Field Paths tell the UI elements how to access the data in the form
  ID,
  NAME: `${CUSTOM_MTA_CONFIG}.${NAME}`,
  API_ENDPOINT: `${CUSTOM_MTA_CONFIG}.${API_ENDPOINT}`,
  CLIENT_ID: `${CUSTOM_MTA_CONFIG}.${AUTH_PARAMS}.${CLIENT_ID}`,
  CLIENT_SECRET: `${CUSTOM_MTA_CONFIG}.${AUTH_PARAMS}.${CLIENT_SECRET}`,
  TOKEN_ENDPOINT: `${CUSTOM_MTA_CONFIG}.${AUTH_PARAMS}.${TOKEN_ENDPOINT}`,
  CUSTOM_HEADERS: `${CUSTOM_MTA_CONFIG}.${CUSTOM_HEADERS}`,
};

export enum Platform {
  ANDROID = 'android',
  IOS = 'ios',
}

const MOBILE_APPS = 'mobileApps';
const PACKAGE_NAME = 'packageName';
const PLATFORM = 'platform';

export const APPLICATIONS_FORM_FIELD_PATHS = {
  MOBILE_APPS,
  ID,
  NAME,
  PACKAGE_NAME,
  PLATFORM,
};

const schema = z.object({
  [ID]: z.number().optional(),
  [CUSTOM_MTA_CONFIG]: z.object({
    [ID]: z.number().optional(),
    [NAME]: z.string().min(1, 'Enter a Name'),
    [API_ENDPOINT]: z.string().url('Enter a valid URL.'),
    [AUTH_PARAMS]: z.object({
      [CLIENT_ID]: z.string().min(1, 'Enter a Client ID'),
      [CLIENT_SECRET]: z.string().min(1, 'Enter a Client Secret'),
      [TOKEN_ENDPOINT]: z.string().url('Enter a valid URL.'),
    }),
    [AUTH_TYPE]: z.nativeEnum(AuthType).default(AuthType.OAUTH2),
    [CUSTOM_HEADERS]: z.array(z.record(z.string().min(1, 'Enter a value'))).default([]),
  }),
  [MOBILE_APPS]: z
    .array(
      z.object({
        [ID]: z.number().optional(),
        [NAME]: z.string().min(1, 'Enter an Application Name'),
        [PACKAGE_NAME]: z.string().min(1, 'Enter a Package Name'),
        [PLATFORM]: z.nativeEnum(Platform).default(Platform.ANDROID),
      }),
    )
    .default([
      {
        [NAME]: '',
        [PACKAGE_NAME]: '',
        [PLATFORM]: Platform.ANDROID,
      },
    ]),
});

type Props = {
  [key: string]: any;
};

export type DestinationFormSchema = z.infer<typeof schema>;

export const DestinationFormProvider: FC<React.PropsWithChildren<Props>> = ({ children }) => {
  const methods = useForm<DestinationFormSchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  return <FormProvider {...methods}> {children} </FormProvider>;
};
